<script setup lang="ts">
import { formatInstantDate } from '@taler/domain'
import { ActionButton, Card, ErrorState, PageShell, StatusTag } from '@taler/ui'
import DetailNotFound from '@/app/components/DetailNotFound.vue'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import {
  canDeleteContact,
  canEditContact,
  ContactAvatar,
  ContactBalances,
  ContactDeleteDialog,
  ContactEditForm,
  ContactHistory,
  ContactPaybackDialog,
  useContact,
  useContactHistory,
} from '@/features/contacts'
import { canCreateEntries } from '@/features/entries'
import { canForgiveContact, ForgiveDialog, forgivableReceivables } from '@/features/forgiveness'
import { canViewTransactions } from '@/features/transactions'
import { useSession } from '@/app/session'
import { useToasts } from '@/app/toasts'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const session = useSession()
const toasts = useToasts()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const contactId = computed(() => route.params['contactId'] as string)
const canDelete = computed(() => canDeleteContact(session.can))
const canEdit = computed(() => canEditContact(session.can))
const isEditing = ref(route.query['edit'] === '1')
const confirmingDelete = ref(false)
if (isEditing.value) void router.replace({ name: 'contact-detail', params: route.params })
const canViewHistory = computed(() => canViewTransactions(session.can))
const canRecordPayback = computed(() => canCreateEntries(session.can))
const canForgive = computed(() => canForgiveContact(session.can))
const forgiveOpen = ref(false)
const ownEntriesOnly = computed(() => canViewHistory.value && !session.can('ENTRY_VIEW_ALL'))
const paybackOpen = ref(false)
const paybackSaved = ref(false)
let paybackSavedTimer: ReturnType<typeof setTimeout> | undefined

const contact = useContact({
  ledgerId: () => session.activeLedger.value?.id,
  contactId: () => contactId.value,
  enabled: () => true,
})

const history = useContactHistory({
  ledgerId: () => session.activeLedger.value?.id,
  accountId: () => contact.contact.value?.accountId,
  enabled: () => canViewHistory.value,
})

const receivables = computed(() => forgivableReceivables(contact.balances.value))
const forgiveSubject = computed(() =>
  contact.contact.value === undefined
    ? undefined
    : {
        target: { kind: 'contact' as const, contactId: contact.contact.value.id },
        name: contact.contact.value.name,
        owed: receivables.value,
      },
)

const updateErrorMessage = computed(() =>
  contact.hasUpdateError.value
    ? (contact.updateErrorDetail.value ?? t('contacts.updateErrorBody'))
    : undefined,
)

// A save that succeeded leaves edit mode; the mutation state is the source of truth for
// "did it work", the same pattern ContactCreateForm uses for its own submit.
watch(
  () => contact.isUpdating.value,
  (updating, wasUpdating) => {
    if (wasUpdating && !updating && !contact.hasUpdateError.value) {
      isEditing.value = false
    }
  },
)

async function remove(): Promise<void> {
  try {
    await contact.deleteContact()
    toasts.push('ok', t('contacts.deleted'))
    await router.push({ name: 'contacts' })
  } catch {
    // surfaced through contact.deleteError
  }
}

function save(input: { name: string; notes: string }): void {
  contact.updateContact(input).catch(() => {
    // surfaced through contact.updateErrorDetail
  })
}

function openDelete(): void {
  contact.resetDelete()
  confirmingDelete.value = true
}

function onForgiven(): void {
  forgiveOpen.value = false
  toasts.push('ok', t('contacts.forgiven'))
}

function onPaybackSaved(): void {
  paybackOpen.value = false
  paybackSaved.value = true
  if (paybackSavedTimer !== undefined) clearTimeout(paybackSavedTimer)
  paybackSavedTimer = setTimeout(() => {
    paybackSaved.value = false
  }, 4000)
}

onBeforeUnmount(() => {
  if (paybackSavedTimer !== undefined) clearTimeout(paybackSavedTimer)
})
</script>

<template>
  <PageShell class="gap-6">
    <p v-if="session.isLoading.value || contact.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <DetailNotFound
      v-else-if="contact.notFound.value"
      :to="{ name: 'contacts' }"
      :list="t('contacts.title')"
    />

    <ErrorState
      v-else-if="contact.error.value"
      :title="t('contacts.detailErrorTitle')"
      :body="t('contacts.errorBody')"
      :retry-label="t('contacts.retry')"
      :retrying="contact.retrying.value"
      @retry="contact.retry()"
    />

    <template v-else-if="contact.contact.value">
      <RouterLink
        :to="{ name: 'contacts' }"
        class="inline-flex w-fit items-center gap-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] underline-offset-4 hover:underline"
      >
        <span aria-hidden="true">←</span>{{ t('contacts.title') }}
      </RouterLink>

      <ContactEditForm
        v-if="isEditing"
        :name="contact.contact.value.name"
        :notes="contact.contact.value.notes"
        :is-submitting="contact.isUpdating.value"
        :error-message="updateErrorMessage"
        @save="save"
        @cancel="isEditing = false"
      />
      <div v-else class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-[var(--space-cell)]">
          <ContactAvatar :id="contact.contact.value.id" :name="contact.contact.value.name" />
          <div class="flex flex-col">
            <h1 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
              {{ contact.contact.value.name }}
            </h1>
            <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
              {{ t('contacts.since') }}
              {{ formatInstantDate(contact.contact.value.createdAt, currentLocale) }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-[var(--space-inline)]">
          <ActionButton v-if="canEdit" type="button" variant="secondary" @click="isEditing = true">
            {{ t('contacts.editAction') }}
          </ActionButton>
          <button
            v-if="canDelete"
            type="button"
            class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-danger)]"
            @click="openDelete"
          >
            {{ t('contacts.deleteAction') }}
          </button>
        </div>
      </div>

      <Card padding="group" class="flex flex-col gap-[var(--space-cell)]">
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
            {{ t('contacts.balances') }}
          </h2>
          <div class="flex items-center gap-[var(--space-inline)]">
            <ActionButton
              v-if="canForgive && receivables.length > 0"
              type="button"
              variant="secondary"
              @click="forgiveOpen = true"
            >
              {{ t('contacts.forgiveAction') }}
            </ActionButton>
            <ActionButton v-if="canRecordPayback" type="button" @click="paybackOpen = true">
              {{ t('contacts.payback.action') }}
            </ActionButton>
          </div>
        </div>
        <ContactBalances :balances="contact.balances.value" />
      </Card>

      <ContactPaybackDialog
        :open="paybackOpen"
        :ledger-id="session.activeLedger.value?.id"
        :contact="contact.contact.value"
        :balances="contact.balances.value"
        @close="paybackOpen = false"
        @saved="onPaybackSaved"
      />

      <ForgiveDialog
        v-if="session.activeLedger.value"
        :open="forgiveOpen"
        :ledger-id="session.activeLedger.value.id"
        :subject="forgiveSubject"
        @close="forgiveOpen = false"
        @forgiven="onForgiven"
      />

      <ContactDeleteDialog
        :open="confirmingDelete"
        :name="contact.contact.value.name"
        :balances="contact.balances.value"
        :is-deleting="contact.isDeleting.value"
        :failed="contact.hasDeleteError.value"
        :denied="contact.deleteDenied.value"
        :error-detail="contact.deleteErrorDetail.value"
        @close="confirmingDelete = false"
        @confirm="remove"
      />

      <Card v-if="canViewHistory" padding="block" class="flex flex-col gap-[var(--space-group)]">
        <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
          {{ ownEntriesOnly ? t('contacts.historyOwnTitle') : t('contacts.historyTitle') }}
        </h2>
        <ContactHistory
          :own-entries-only="ownEntriesOnly"
          :rows="history.rows.value"
          :is-loading="history.isLoading.value"
          :is-fetching-next-page="history.isFetchingNextPage.value"
          :has-next-page="history.hasNextPage.value"
          :error="history.error.value"
          @load-more="history.fetchNextPage()"
          :retrying="history.retrying.value"
          @retry="history.retry()"
        />
      </Card>

      <Card v-if="!isEditing" padding="block" class="flex flex-col gap-[var(--space-inline)]">
        <div class="flex items-center justify-between gap-3">
          <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
            {{ t('contacts.notes') }}
          </h2>
          <ActionButton v-if="canEdit" type="button" variant="secondary" @click="isEditing = true">
            {{ t('contacts.editAction') }}
          </ActionButton>
        </div>
        <p v-if="contact.contact.value.notes" class="whitespace-pre-wrap text-[color:var(--color-text)]">
          {{ contact.contact.value.notes }}
        </p>
        <p v-else class="text-[color:var(--color-text-muted)]">{{ t('contacts.notesEmpty') }}</p>
      </Card>
    </template>

    <div
      v-if="paybackSaved"
      role="status"
      class="fixed bottom-6 right-6 z-[70] rounded-[var(--radius-overlay)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-[var(--space-group)] py-[var(--space-cell)] shadow-[var(--shadow-overlay)]"
    >
      <StatusTag tone="ok">{{ t('contacts.payback.success') }}</StatusTag>
    </div>
  </PageShell>
</template>
